import { Link } from "react-router-dom";
import type { components } from "../types/api.gen";

export type BracketMatch = components["schemas"]["FinalsMatch"];

interface BracketViewProps {
  matches: BracketMatch[];
  onCreateGame?: (matchId: string) => void;
}

function MatchCard({
  match,
  onCreateGame,
}: {
  match: BracketMatch;
  onCreateGame?: (matchId: string) => void;
}) {
  const isPending = match.status === "pending";
  const isCompleted = match.status === "completed";
  const isReady = match.status === "ready" && !match.gameId;
  const hasGame = !!match.gameId;

  const card = (
    <div
      className={`rounded-xl border-2 bg-white px-3 py-2 text-sm transition ${
        isCompleted
          ? "border-ember/30"
          : isPending
            ? "border-dashed border-gray-200"
            : "border-gray-200"
      }`}
    >
      <TeamRow
        name={match.leftTeam ?? null}
        score={match.leftScore ?? null}
        isWinner={match.winner === "left"}
        isPending={isPending}
      />
      <div className="my-1 border-t border-gray-100" />
      <TeamRow
        name={match.rightTeam ?? null}
        score={match.rightScore ?? null}
        isWinner={match.winner === "right"}
        isPending={isPending}
      />
      {isReady && onCreateGame && (
        <button
          type="button"
          className="mt-2 w-full rounded-lg bg-ember/10 py-1 text-xs font-medium text-ember hover:bg-ember/20 transition"
          onClick={() => onCreateGame(match.id)}
        >
          Créer la partie
        </button>
      )}
      {hasGame && (
        <Link
          to={`/games/${match.gameId}`}
          className="mt-2 block w-full rounded-lg bg-gray-50 py-1 text-center text-xs font-medium text-smoke hover:text-ink transition"
        >
          Voir la partie
        </Link>
      )}
    </div>
  );

  return card;
}

function TeamRow({
  name,
  score,
  isWinner,
  isPending,
}: {
  name: string | null;
  score: number | null;
  isWinner: boolean;
  isPending: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <span
        className={`truncate ${
          isWinner
            ? "font-bold text-ember"
            : isPending
              ? "text-gray-300 italic"
              : "text-ink"
        }`}
      >
        {isWinner && "🏆 "}
        {name ?? "En attente"}
      </span>
      {score !== null && (
        <span
          className={`shrink-0 tabular-nums ${
            isWinner ? "font-bold text-ember" : "text-smoke"
          }`}
        >
          {score}
        </span>
      )}
    </div>
  );
}

function RoundLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-2 text-center text-xs font-semibold uppercase tracking-wider text-smoke">
      {children}
    </p>
  );
}

export function BracketView({ matches, onCreateGame }: BracketViewProps) {
  const sf1 = matches.find((m) => m.round === "semi_final" && m.position === 1);
  const sf2 = matches.find((m) => m.round === "semi_final" && m.position === 2);
  const final = matches.find((m) => m.round === "final");
  const petitFinal = matches.find((m) => m.round === "petit_final");

  if (!sf1 || !sf2 || !final || !petitFinal) {
    return (
      <p className="text-sm text-smoke">
        La phase finale n'a pas encore été générée.
      </p>
    );
  }

  return (
    <div className="space-y-8">
      {/* Winners bracket */}
      <div className="relative grid grid-cols-[1fr_auto_1fr] items-center gap-x-0 gap-y-0">
        {/* Semi-finals column */}
        <div className="space-y-4 pr-2">
          <div>
            <RoundLabel>Demi-finale 1</RoundLabel>
            <MatchCard match={sf1} onCreateGame={onCreateGame} />
          </div>
          <div>
            <RoundLabel>Demi-finale 2</RoundLabel>
            <MatchCard match={sf2} onCreateGame={onCreateGame} />
          </div>
        </div>

        {/* Connector lines */}
        <div className="relative flex h-full w-8 flex-col items-center justify-center">
          {/* Top arm */}
          <div className="absolute top-[25%] right-0 left-0 h-[1px] bg-gray-300" />
          {/* Bottom arm */}
          <div className="absolute bottom-[25%] right-0 left-0 h-[1px] bg-gray-300" />
          {/* Vertical connector */}
          <div className="absolute top-[25%] bottom-[25%] right-0 w-[1px] bg-gray-300" />
          {/* Arrow to final */}
          <div className="absolute top-1/2 right-0 h-[1px] w-2 -translate-y-1/2 bg-gray-300" />
        </div>

        {/* Final column */}
        <div className="flex items-center pl-2">
          <div className="w-full">
            <RoundLabel>Finale</RoundLabel>
            <MatchCard match={final} onCreateGame={onCreateGame} />
          </div>
        </div>
      </div>

      {/* Losers bracket (petite finale) */}
      <div>
        <RoundLabel>Petite finale</RoundLabel>
        <div className="mx-auto max-w-xs">
          <MatchCard match={petitFinal} onCreateGame={onCreateGame} />
        </div>
      </div>
    </div>
  );
}
